import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "setting-logic-page" }
const _hoisted_2 = { class: "setting-logic-page__header" }
const _hoisted_3 = { class: "container" }
const _hoisted_4 = { class: "setting-content" }
const _hoisted_5 = { class: "dashboard-alert" }
const _hoisted_6 = { class: "simulation-sales-result-wdesc" }
const _hoisted_7 = { class: "simulation-sales-result-wdesc__button" }
const _hoisted_8 = { class: "simulation-sales-result-popover" }
const _hoisted_9 = { class: "save" }

import { loadMyPropertyPreferences, myPropertyPreferences, saveMyPropertyPreferences } from '@/appLogic/view/preferences';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { openFailToSavedModal, openSuccessToSaveModal } from '@/corePagesModule/components/modal/modalActions';
import ToggleSwitch from '@/corePagesModule/components/ToggleSwitch.vue';
import { diContainer } from '@/coreContainer/container';
import { i18n } from '@/i18n';
import SettingsPageTitle from '@/settingsModule/components/SettingsPageTitle.vue';
import { computed, onBeforeMount, reactive } from 'vue';
import { useRouter } from 'vue-router';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import { VPopover } from 'v-tooltip';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';

interface SettingLogicState {
  clientSettingState: {
    isEdited: boolean;
    settings: Record<string, boolean | undefined>;
  };
  serverSettingState: {
    isEdited: boolean;
    settings: Record<string, boolean | undefined>;
  };
  isFetchingErrorOccured: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SettingLogicPage',
  setup(__props) {

const t = i18n.t;
const router = useRouter();

/**
 * このページで使用するStateのinterface
 */
const state = reactive<SettingLogicState>({
  clientSettingState: {
    isEdited: false,
    settings: { unpaidOrderedOrderAppearance: undefined, orderReminderAppearance: undefined },
  },
  serverSettingState: {
    isEdited: false,
    settings: { useRoundDown: undefined, useCurrentMonthSalesResult: undefined },
  },
  isFetchingErrorOccured: false,
});

const isEdited = computed(() => state.clientSettingState.isEdited || state.serverSettingState.isEdited);

/**
 * isEditedをリセット
 */
const resetIsEdited = () => {
  state.clientSettingState.isEdited = false;
  state.serverSettingState.isEdited = false;
};

/**
 * 設定のトグルを押した際のハンドラ
 * @param toggledKey - トグルを押した項目
 */
const onToggleClientSetting = (toggledKey: keyof SettingLogicState['clientSettingState']['settings']) => {
  if (!(toggledKey in myPropertyPreferences.order)) {
    return;
  }
  state.clientSettingState.isEdited = true;
  state.clientSettingState.settings[toggledKey] = !state.clientSettingState.settings[toggledKey];
};

/**
 * トグルを押した際のハンドラ
 * @param type - 押したトグル
 */
const onToggleServerSetting = (type: keyof SettingLogicState['serverSettingState']['settings']) => {
  state.serverSettingState.isEdited = true;
  state.serverSettingState.settings[type] = !state.serverSettingState.settings[type];
};

/**
 * 保存ボタンを押した際の処理
 */
const onSave = async () => {
  if (
    state.serverSettingState.isEdited &&
    !(await showInfoModal({
      modalTitle: t('組織全体の設定が変更されています'),
      modalDescription: t('シミュレーション設定は組織内全てのユーザーに対して反映されます。よろしいですか？'),
      nextButtonLabel: t('続行する'),
      backButtonLabel: t('キャンセル'),
    }))
  )
    return;

  for (const [key, value] of Object.entries(state.clientSettingState.settings)) {
    if (value == null) continue;
    myPropertyPreferences.order[key] = value;
  }
  const results = await waitLoading(Promise.all([saveMyPropertyPreferences(), saveToRDS()]), t('保存中…'), '');

  if (results == null) {
    await openFailToSavedModal();
    return;
  }

  const [indexedDbResult, rdsResult] = results;
  const clientSettingsSaved = !state.clientSettingState.isEdited || indexedDbResult != null;
  const serverSettingsSaved = !state.serverSettingState.isEdited || rdsResult?.status.type === 'success';
  const simulationStatus = rdsResult?.status.type === 'success' ? rdsResult.status.data.simulationStatus : undefined;
  if (simulationStatus === 'failed' || simulationStatus === 'unknown') {
    const simulationMessage =
      simulationStatus === 'failed'
        ? t(
            '設定変更後のシミュレーションに失敗しました。予在庫や発注推奨数量に変更が反映されていない可能性があります。しばらく時間をおいて再度設定を保存してください。解決しない場合はお問い合わせください。'
          )
        : t(
            '設定変更後のシミュレーション完了を確認できませんでした。シミュレーション処理が継続している可能性があるため、しばらく時間をおいて結果をご確認ください。変更が適用されていない場合、再度設定を保存してください。解決しない場合はお問い合わせください。'
          );
    await showInfoModal({
      modalTitle:
        simulationStatus === 'failed' ? t('シミュレーションの実行に失敗しました') : t('シミュレーションの完了を確認できませんでした'),
      modalDescription: clientSettingsSaved ? simulationMessage : `${simulationMessage}\n${t('一部の設定を保存できませんでした')}`,
      backButtonLabel: '',
      nextButtonLabel: t('OK'),
      enableOutsideClick: false,
      isCenterContent: true,
      useKeyboard: false,
    });
  } else if (clientSettingsSaved && serverSettingsSaved) {
    await openSuccessToSaveModal();
    location.reload();
  } else if (indexedDbResult == null && rdsResult == null) {
    await openFailToSavedModal();
  } else {
    showInfoModal({
      modalTitle: t('一部の設定を保存できませんでした'),
      modalDescription: t('ページを再読み込みして、もう一度お試しください。'),
      backButtonLabel: '',
      nextButtonLabel: t('OK'),
      enableOutsideClick: false,
      isCenterContent: true,
      useKeyboard: false,
    });
  }
  if (clientSettingsSaved) state.clientSettingState.isEdited = false;
  if (serverSettingsSaved) state.serverSettingState.isEdited = false;
};

/**
 * RDSに設定を保存する
 */
const saveToRDS = async () => {
  if (
    state.serverSettingState.isEdited &&
    !state.isFetchingErrorOccured &&
    state.serverSettingState.settings.useRoundDown != null &&
    state.serverSettingState.settings.useCurrentMonthSalesResult != null
  ) {
    return await diContainer.putSimulationSettingUseCase.execute({
      useRoundDown: state.serverSettingState.settings.useRoundDown,
      useCurrentMonthSalesResult: state.serverSettingState.settings.useCurrentMonthSalesResult,
    });
  }
  return null;
};

/**
 * ダッシュボードアラート設定ボタン押下
 */
const onClickDashboardAlertSetting = () => {
  router.push('logic/dashboard-alert');
};

onBeforeMount(async () => {
  resetIsEdited();

  const [_, simulationSettingResult] = (await waitLoading(
    Promise.all([loadMyPropertyPreferences(), diContainer.getSimulationSettingUseCase.execute()]),
    undefined,
    ''
  )) ?? [null, null];

  for (const key of Object.keys(state.clientSettingState.settings)) {
    if (key in myPropertyPreferences.order) {
      state.clientSettingState.settings[key] = myPropertyPreferences.order[key] ?? true;
    }
  }

  // サーバーから設定を取得
  if (simulationSettingResult != null && simulationSettingResult.status.type === 'success') {
    state.serverSettingState.settings.useRoundDown = simulationSettingResult.status.data.useRoundDown;
    state.serverSettingState.settings.useCurrentMonthSalesResult = simulationSettingResult.status.data.useCurrentMonthSalesResult;
  } else {
    state.isFetchingErrorOccured = true;
    showInfoModal({
      modalTitle: t('一部設定の取得に失敗しました'),
      modalDescription: t(
        'しばらく時間をおいてから、ページを再読み込みしてください。再読み込みしても取得できない場合は、お問い合わせください。'
      ),
    });
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _createVNode(SettingsPageTitle, {
        label: _unref(t)('設定 > 発注・計算ロジック')
      }, null, 8, ["label"])
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createVNode(ToggleSwitch, {
          "is-enabled": state.clientSettingState.settings.unpaidOrderedOrderAppearance,
          onOnToggled: _cache[0] || (_cache[0] = ($event: any) => (onToggleClientSetting('unpaidOrderedOrderAppearance')))
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('最新在庫の日付以前の納期を持つ未入荷の発注を検知した場合、アラートで通知する')), 1)
          ], undefined, true),
          _: 1
        }, 8, ["is-enabled"]),
        _createVNode(ToggleSwitch, {
          "is-enabled": state.clientSettingState.settings.orderReminderAppearance,
          onOnToggled: _cache[1] || (_cache[1] = ($event: any) => (onToggleClientSetting('orderReminderAppearance')))
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('発注登録・保存後にリマインド設定を表示する')), 1)
          ], undefined, true),
          _: 1
        }, 8, ["is-enabled"]),
        _createElementVNode("div", _hoisted_5, [
          _createTextVNode(_toDisplayString(_unref(t)('ダッシュボードのアラート設定')) + " ", 1),
          _createVNode(TextButton, {
            "button-style": "btn-secondary",
            onClick: onClickDashboardAlertSetting
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)('詳細設定')), 1)
            ], undefined, true),
            _: 1
          })
        ]),
        _createElementVNode("h3", null, _toDisplayString(_unref(t)('シミュレーション設定（組織単位）')), 1),
        _createVNode(ToggleSwitch, {
          "is-enabled": state.serverSettingState.settings.useRoundDown,
          disabled: state.isFetchingErrorOccured,
          onOnToggled: _cache[2] || (_cache[2] = ($event: any) => (onToggleServerSetting('useRoundDown')))
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('発注推奨数量の端数を切り捨てる（オフの場合は切り上げ）')), 1)
          ], undefined, true),
          _: 1
        }, 8, ["is-enabled", "disabled"]),
        _createElementVNode("div", null, [
          _createVNode(ToggleSwitch, {
            "is-enabled": state.serverSettingState.settings.useCurrentMonthSalesResult,
            disabled: state.isFetchingErrorOccured,
            onOnToggled: _cache[3] || (_cache[3] = ($event: any) => (onToggleServerSetting('useCurrentMonthSalesResult')))
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_6, [
                _createTextVNode(_toDisplayString(_unref(t)('シミュレーションで当月の販売実績を考慮する')) + " ", 1),
                _createVNode(_unref(VPopover), {
                  placement: "bottom-start",
                  offset: 4,
                  "auto-hide": true,
                  class: "simulation-sales-result-wdesc__popover"
                }, {
                  popover: _withCtx(() => [
                    _createElementVNode("div", _hoisted_8, _toDisplayString(`${_unref(t)('オンにすると、登録済みの当月販売実績を反映してシミュレーションします。')}\n${_unref(t)(
                        '現在の販売状況に応じて、予在庫推移や発注推奨数量が変わる場合があります。'
                      )}\n${_unref(t)('当月販売実績が登録されていない場合、シミュレーション結果は変わりません。')}`), 1)
                  ]),
                  default: _withCtx(() => [
                    _createElementVNode("button", _hoisted_7, [
                      _createVNode(Mdi, {
                        icon: "information",
                        variant: "outlined"
                      })
                    ])
                  ], undefined, true),
                  _: 1
                })
              ])
            ], undefined, true),
            _: 1
          }, 8, ["is-enabled", "disabled"])
        ])
      ]),
      _createElementVNode("div", _hoisted_9, [
        _createElementVNode("span", {
          class: _normalizeClass(["save__text", { invisible: !isEdited.value }])
        }, _toDisplayString(_unref(t)('未保存の設定変更があります')), 3),
        _createVNode(TextButton, { onClick: onSave }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('保存する')), 1)
          ], undefined, true),
          _: 1
        })
      ])
    ])
  ]))
}
}

})